import type { RootState } from '@/rootReducer';
import React, { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseThin } from '@/resources/assets/images';

import { createUserAccounts } from '@/modules/user/userActions';

import {
	getCompanies,
	createCompany,
	updateCompany,
	clearCompanyErrors,
	getCompanyHubspotId
} from '@/modules/company/companyActions';

import './styles.scss';
import HubspotCompanySelector from './HubspotSelector';

interface Company {
	id: string;
	companyHsId: string;
	name: string;
}

interface CreateUser {
	email: string;
	name: string;
	password: string;
	role: string;
	tier: string;
}
interface Hscompany {
	companyName: string;
	companyHsId: string;
}

interface MarketCoordinate {
	lat: string;
	lng: string;
	locationName: string;
	streetAddress: string;
	cityName: string;
	stateName: string;
	zipcode: string;
	radius: string;
	marketType: string;
}

interface AdminCreateUsersPayload {
	createUsers: CreateUser[];
	locationCoordinates: MarketCoordinate[];
	company: Company;
	companyHsId: string;
}

interface AdminAccountCreateProps {
	showAccountCreation: boolean;
	onClose: () => void;
	setShowAccountCreate: Dispatch<SetStateAction<boolean>>;
}

const stateList = [
	// USA
	'AL',
	'AK',
	'AZ',
	'AR',
	'CA',
	'CO',
	'CT',
	'DE',
	'FL',
	'GA',
	'HI',
	'ID',
	'IL',
	'IN',
	'IA',
	'KS',
	'KY',
	'LA',
	'ME',
	'MD',
	'MA',
	'MI',
	'MN',
	'MS',
	'MO',
	'MT',
	'NE',
	'NV',
	'NH',
	'NJ',
	'NM',
	'NY',
	'NC',
	'ND',
	'OH',
	'OK',
	'OR',
	'PA',
	'RI',
	'SC',
	'SD',
	'TN',
	'TX',
	'UT',
	'VT',
	'VA',
	'WA',
	'WV',
	'WI',
	'WY',
	// CA
	'AB',
	'BC',
	'MB',
	'NB',
	'NL',
	'NS',
	'ON',
	'PE',
	'QC',
	'SK',
	'NT',
	'NU',
	'YT'
];

const AdminAccountCreate: React.FC<AdminAccountCreateProps> = ({
	showAccountCreation,
	onClose,
	setShowAccountCreate
}) => {
	const dispatch = useDispatch();

	// Selectors
	const companies = useSelector((state: RootState) =>
		state.company.getIn(['companies', 'data'])
	);
	const createdCompany = useSelector((state: RootState) =>
		state.company.getIn(['createCompanies', 'data'])
	);
	const createdCompanyError = useSelector((state: RootState) =>
		state.company.getIn(['createCompanies', 'error'])
	);
	const updateCompanyData = useSelector((state: RootState) =>
		state.company.getIn(['updateCompanies', 'data'])
	);
	const updateCompanyError = useSelector((state: RootState) =>
		state.company.getIn(['updateCompanies', 'error'])
	);
	const createdUsersData = useSelector((state: RootState) =>
		state.user.getIn(['createUsers', 'data'])
	);
	const createdUsersLoading = useSelector((state: RootState) =>
		state.user.getIn(['createUsers', 'loading'])
	);
	const createdUsersError = useSelector((state: RootState) =>
		state.user.getIn(['createUsers', 'error'])
	);
	const hubspotIdforDisplay = useSelector((state: RootState) =>
		state.company.getIn(['hubspotCompanyId', 'data'])
	);

	// State variables
	const [filteredCompanies, setFilteredCompanies] = useState(companies);
	const [marketCoordinates, setMarketCoordinates] = useState<
		MarketCoordinate[]
	>([
		{
			lat: '',
			lng: '',
			locationName: '',
			streetAddress: '',
			cityName: '',
			stateName: '',
			zipcode: '',
			radius: '',
			marketType: 'Locational'
		}
	]);
	const [locationCoordinates, setLocationCoordinates] = useState<
		MarketCoordinate[]
	>([
		{
			lat: '',
			lng: '',
			locationName: '',
			streetAddress: '',
			cityName: '',
			stateName: '',
			zipcode: '',
			radius: '',
			marketType: 'Locational'
		}
	]);
	const [companyExists, setCompanyExists] = useState(false);
	const [selectedCompany, setSelectedCompany] = useState('');
	const [companyHsId, setCompanyHsId] = useState('');
	const [hsidForDisplay, sethsIdforDisplay] = useState<string>('');
	const [searchInput, setSearchInput] = useState('');
	const [stateSearchTerm, setStateSearchTerm] = useState('');
	const [isStateDropdownOpen, setIsStateDropdownOpen] = useState<boolean[]>(
		[]
	);
	const [filteredStateOptions, setFilteredStateOptions] = useState<string[]>(
		[]
	);
	const [displayError, setDisplayError] = useState('');
	const [isDropdownVisible, setIsDropdownVisible] = useState(false);
	const [createUsers, setCreateUsers] = useState<CreateUser[]>([
		{ email: '', name: '', password: '', role: 'User', tier: 'Gold' }
	]);
	const [placeholder, setPlaceholder] = useState<string>(
		'Search for company...'
	);
	const [missingHsIdmessage, setMissingHsIdMessage] = useState<string>('');

	// Action dispatchers
	const adminGetCompanies = () => dispatch(getCompanies());
	const adminCreateCompanies = (payload: {
		name: string;
		marketCoordinates: MarketCoordinate[];
		companyHsId: string;
	}) => dispatch(createCompany(payload));
	const adminUpdateCompanies = (payload: {
		name: string;
		marketCoordinates: MarketCoordinate[];
		companyHsId: string;
	}) => dispatch(updateCompany(payload));
	const adminCreateUserAccounts = (payload: AdminCreateUsersPayload) =>
		dispatch(createUserAccounts(payload));

	// Effects
	useEffect(() => {
		const filteredStates = stateList.filter(state =>
			state.toLowerCase().includes(stateSearchTerm.toLowerCase())
		);
		setFilteredStateOptions(filteredStates);
	}, [stateSearchTerm]);

	useEffect(() => {
		adminGetCompanies();
		if (companies.length > 0 && companies[0].name) {
			setFilteredCompanies(companies);
		}
	}, []);

	useEffect(() => {
		if (companies && searchInput) {
			const filtered = companies.filter((company: Company) => {
				return (
					company.companyHsId &&
					(company.name
						?.toLowerCase()
						.includes(searchInput.toLowerCase()) ||
						company.companyHsId
							?.toLowerCase()
							.includes(searchInput.toLowerCase()))
				);
			});

			setFilteredCompanies(filtered);
			setIsDropdownVisible(filtered.length > 0);
		} else {
			setFilteredCompanies([]);
			setIsDropdownVisible(false);
		}
	}, [searchInput]);

	useEffect(() => {
		if (
			createdCompany &&
			createdCompany.data.message === 'Company created successfully'
		) {
			if (createUsers.length > 0) {
				adminCreateUserAccounts({
					createUsers,
					companyHsId,
					locationCoordinates,
					company: createdCompany.data.company
				});
			}
		}
		if (
			updateCompanyData &&
			updateCompanyData.data.message === 'Company updated successfully'
		) {
			if (createUsers.length > 0) {
				adminCreateUserAccounts({
					createUsers,
					companyHsId,
					locationCoordinates,
					company: updateCompanyData.data.company
				});
			}
		}
	}, [
		createdCompany,
		createdCompanyError,
		updateCompanyData,
		updateCompanyError
	]);

	useEffect(() => {
		setDisplayError(createdUsersError);
	}, [createdUsersError]);

	useEffect(() => {
		if (hubspotIdforDisplay) {
			sethsIdforDisplay(hubspotIdforDisplay);
		}
	}, [hubspotIdforDisplay]);

	// Handlers
	const createAccount = () => {
		dispatch(clearCompanyErrors());

		if (!companyHsId) {
			setMissingHsIdMessage('Must first select a hubspot company!');
			setTimeout(() => {
				setMissingHsIdMessage('');
			}, 3000);

			return;
		}

		const companyName = selectedCompany || '';
		if (companyExists) {
			if (createUsers.length < 1 && marketCoordinates.length < 1) {
				setDisplayError(
					'Please add at least one user or one market to update a company'
				);
				return;
			}
			adminUpdateCompanies({
				name: companyName,
				companyHsId,
				marketCoordinates
			});
		} else {
			adminCreateCompanies({
				name: companyName,
				companyHsId,
				marketCoordinates
			});
		}
	};

	const handleAddLocationCoordinates = () => {
		setLocationCoordinates([
			...locationCoordinates,
			{
				lat: '',
				lng: '',
				locationName: '',
				streetAddress: '',
				cityName: '',
				stateName: '',
				zipcode: '',
				radius: '',
				marketType: 'Locational'
			}
		]);
	};

	const handleMarketCopy = () => {
		const locationalCoordinates = marketCoordinates.filter(
			coord => coord.marketType === 'Locational'
		);
		setLocationCoordinates(locationalCoordinates);
	};

	const handleAddMarketCoordinates = () => {
		setMarketCoordinates([
			...marketCoordinates,
			{
				lat: '',
				lng: '',
				locationName: '',
				streetAddress: '',
				cityName: '',
				stateName: '',
				zipcode: '',
				radius: '',
				marketType: 'Locational'
			}
		]);
	};

	const handleMarketCoordinateChange = (
		index: number,
		field: string,
		value: string
	) => {
		let newCoordinates = [];
		// If the change is a marketType (state, national, location) we want to clear all fields
		if (field === 'marketType') {
			newCoordinates = marketCoordinates.map((coord, i) =>
				i === index
					? {
							lat: '',
							lng: '',
							locationName: '',
							streetAddress: '',
							cityName: '',
							stateName: '',
							zipcode: '',
							radius: '',
							[field]: value
						}
					: coord
			);
		} else {
			newCoordinates = marketCoordinates.map((coord, i) =>
				i === index ? { ...coord, [field]: value } : coord
			);
		}

		// When setting to national we default to display USA, ensure that if that is the national market we want
		// we properly set the field to contain this default value
		if (field === 'marketType' && value === 'National') {
			newCoordinates[index].locationName = 'USA';
		}

		setMarketCoordinates(newCoordinates);
	};

	const handleLocationCoordinateChange = (
		index: number,
		field: string,
		value: string
	) => {
		const newCoordinates = locationCoordinates.map((coord, i) =>
			i === index ? { ...coord, [field]: value } : coord
		);
		setLocationCoordinates(newCoordinates);
	};

	const handleRemoveMarketCoordinates = (index: number) => {
		setMarketCoordinates(marketCoordinates.filter((_, i) => i !== index));
	};

	const handleRemoveLocationCoordinates = (index: number) => {
		setLocationCoordinates(
			locationCoordinates.filter((_, i) => i !== index)
		);
	};

	const handleCompanyExistsChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		dispatch(clearCompanyErrors());
		sethsIdforDisplay('');
		setCompanyExists(event.target.value === 'yes');
		setCompanyHsId('');
		setPlaceholder("Enter company's name");
		setSelectedCompany('');
	};

	const handleSelectCompany = (company: Company) => {
		dispatch(getCompanyHubspotId({ companyId: company.id }));
		setCompanyHsId(company.companyHsId);
		setFilteredCompanies([]);
		setSearchInput('');
		setSelectedCompany(company.name);
		setIsDropdownVisible(false);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(event.target.value);
		setIsDropdownVisible(true);
	};

	const setGenericPassword = () => {
		const newPassword =
			(
				document.getElementById(
					'user-generic-password'
				) as HTMLInputElement
			).value || '';
		const updatedUsers = createUsers.map(user => ({
			...user,
			password: newPassword
		}));
		setCreateUsers(updatedUsers);
	};

	// Hadler for the add user button, sets default values for new user
	const handleAddUser = () => {
		setCreateUsers([
			...createUsers,
			{ email: '', name: '', password: '', role: 'User', tier: 'Gold' }
		]);
	};

	const handleRemoveUser = (index: number) => {
		setCreateUsers(createUsers.filter((_, i) => i !== index));
	};

	const handleUserChange = (index: number, field: string, value: string) => {
		const newUsers = createUsers.map((user, i) =>
			i === index ? { ...user, [field]: value } : user
		);
		setCreateUsers(newUsers);
	};

	const handleMarketStateSelect = (state: string, index: number) => {
		handleMarketCoordinateChange(index, 'stateName', state);
		toggleStateDropdown(index, false);
		setStateSearchTerm(state);
	};

	const toggleStateDropdown = (index: number, isOpen: boolean) => {
		const newDropdownStates = [...isStateDropdownOpen];
		newDropdownStates[index] = isOpen;
		setIsStateDropdownOpen(newDropdownStates);
	};

	const handleClose = () => {
		setShowAccountCreate(false);
		setCompanyExists(false);
		onClose();
	};

	if (!showAccountCreation) return null;

	return (
		<div className="admin-create-acc">
			<div className="admin-create-acc-header">
				Create an account
				<button
					className="modal-close-button admin-cancel-scheduled-close-button flex h-full"
					type="button"
				>
					<CloseThin
						width={18}
						height={18}
						className="text-white"
						onClick={handleClose}
					/>
				</button>
			</div>
			{/* User input fields */}
			<div className="admin-create-acc-header">Users</div>
			<div className="coordinates-container">
				{createUsers.length > 0 &&
					createUsers.map((user, index) => (
						<div key={index} className="coordinate-inputs">
							<input
								type="text"
								id="create-acc-email-input"
								placeholder="Enter email"
								value={user.email || ''}
								onChange={e =>
									handleUserChange(
										index,
										'email',
										e.target.value
									)
								}
							/>
							<input
								type="text"
								placeholder="Enter user's name"
								id="create-acc-user-name"
								value={user.name || ''}
								onChange={e =>
									handleUserChange(
										index,
										'name',
										e.target.value
									)
								}
							/>
							<input
								type="text"
								placeholder="Enter user's password"
								id="create-acc-user-password"
								value={user.password || ''}
								onChange={e =>
									handleUserChange(
										index,
										'password',
										e.target.value
									)
								}
							/>
							<select
								className="role-select"
								value={user.role || 'User'}
								onChange={e =>
									handleUserChange(
										index,
										'role',
										e.target.value
									)
								}
							>
								<option value="User">User</option>
								<option value="Manager">Manager</option>
							</select>
							<select
								className="role-select"
								value={user.tier || 'Gold'}
								onChange={e =>
									handleUserChange(
										index,
										'tier',
										e.target.value
									)
								}
							>
								<option value="Gold">Gold</option>
								<option value="Platinum">Platinum</option>
								<option value="Diamond">Diamond</option>
							</select>
							<button
								type="button"
								onClick={() => handleRemoveUser(index)}
								className="remove-user-button"
							>
								<CloseThin
									width={18}
									height={18}
									className="text-white"
								/>
							</button>
						</div>
					))}
				<div className="acc-create-container">
					<button
						onClick={handleAddUser}
						className="account-creation-button"
					>
						Add another user
					</button>
					<input
						type="text"
						placeholder="Enter user's password"
						id="user-generic-password"
					/>
					<button
						onClick={setGenericPassword}
						className="account-creation-button"
					>
						Set password for all users
					</button>
				</div>
			</div>
			{/* Coordinate input fields */}
			<div className="admin-create-acc-header">Markets</div>
			<div className="coordinates-container">
				{marketCoordinates.length > 0 &&
					marketCoordinates.map((coord, index) => (
						<div key={index} className="coordinate-row">
							<select
								className="role-select"
								value={coord.marketType || 'Locational'}
								onChange={e =>
									handleMarketCoordinateChange(
										index,
										'marketType',
										e.target.value
									)
								}
							>
								<option value="Locational">Locational</option>
								<option value="State">State</option>
								<option value="National">National</option>
							</select>
							{/* Locational specific inputs */}
							{coord.marketType === 'Locational' && (
								<div key={index} className="coordinate-inputs">
									<input
										type="number"
										placeholder="Latitude"
										value={coord.lat || ''}
										onChange={e =>
											handleMarketCoordinateChange(
												index,
												'lat',
												e.target.value
											)
										}
									/>
									<input
										type="number"
										placeholder="Longitude"
										value={coord.lng || ''}
										onChange={e =>
											handleMarketCoordinateChange(
												index,
												'lng',
												e.target.value
											)
										}
									/>
									<input
										type="text"
										placeholder="Loc. Name"
										className="location-name-input"
										value={coord.locationName || ''}
										onChange={e =>
											handleMarketCoordinateChange(
												index,
												'locationName',
												e.target.value
											)
										}
									/>
									<input
										type="number"
										placeholder="Radius"
										value={coord.radius || ''}
										onChange={e =>
											handleMarketCoordinateChange(
												index,
												'radius',
												e.target.value
											)
										}
									/>
								</div>
							)}
							{coord.marketType === 'State' && (
								<div
									key={index}
									className="coordinate-inputs-state"
								>
									<input
										type="text"
										value={coord.stateName}
										onClick={() =>
											toggleStateDropdown(
												index,
												!isStateDropdownOpen[index]
											)
										}
										onChange={e => {
											setStateSearchTerm(e.target.value);
											toggleStateDropdown(index, true);
											handleMarketCoordinateChange(
												index,
												'stateName',
												e.target.value
											);
										}}
										placeholder="Select State"
									/>
									{isStateDropdownOpen[index] &&
										filteredStateOptions.length > 0 && (
											<ul className="acc-create-dropdown-list-state">
												{filteredStateOptions.map(
													(state, idx) => (
														<li
															key={idx}
															onClick={() => {
																handleMarketStateSelect(
																	state,
																	index
																);
															}}
														>
															{state}
														</li>
													)
												)}
											</ul>
										)}
								</div>
							)}
							{coord.marketType === 'National' && (
								<div
									key={index}
									className="coordinate-inputs-state"
								>
									<select
										className="role-select"
										value={coord.locationName || 'USA'}
										onChange={e =>
											handleMarketCoordinateChange(
												index,
												'locationName',
												e.target.value
											)
										}
									>
										<option value="USA">USA</option>
										<option value="CA">Canada</option>
										<option value="PR">Puerto Rico</option>
									</select>
								</div>
							)}

							<button
								type="button"
								onClick={() =>
									handleRemoveMarketCoordinates(index)
								}
								className="remove-coordinate-button"
							>
								<CloseThin
									width={18}
									height={18}
									className="text-white"
								/>
							</button>
						</div>
					))}

				<div className="button-radio-container">
					<button
						onClick={handleAddMarketCoordinates}
						className="account-creation-button"
					>
						Add another market
					</button>
					<button
						onClick={handleMarketCopy}
						className="account-creation-button"
					>
						Copy Locational markets to Locations
					</button>
				</div>
			</div>
			{/* Coordinate input fields */}
			<div className="admin-create-acc-header">Locations</div>
			<div className="coordinates-container">
				{locationCoordinates.length > 0 &&
					locationCoordinates.map((coord, index) => (
						<div key={index} className="coordinate-inputs">
							<input
								type="number"
								placeholder="Latitude"
								value={coord.lat || ''}
								onChange={e =>
									handleLocationCoordinateChange(
										index,
										'lat',
										e.target.value
									)
								}
							/>
							<input
								type="number"
								placeholder="Longitude"
								value={coord.lng || ''}
								onChange={e =>
									handleLocationCoordinateChange(
										index,
										'lng',
										e.target.value
									)
								}
							/>
							<input
								type="text"
								placeholder="Loc. Name"
								className="location-name-input"
								value={coord.locationName || ''}
								onChange={e =>
									handleLocationCoordinateChange(
										index,
										'locationName',
										e.target.value
									)
								}
							/>
							<input
								type="number"
								placeholder="Radius"
								value={coord.radius || ''}
								onChange={e =>
									handleLocationCoordinateChange(
										index,
										'radius',
										e.target.value
									)
								}
							/>
							<button
								type="button"
								onClick={() =>
									handleRemoveLocationCoordinates(index)
								}
								className="remove-coordinate-button"
							>
								<CloseThin
									width={18}
									height={18}
									className="text-white"
								/>
							</button>
						</div>
					))}
				<div className="button-radio-container">
					<button
						onClick={handleAddLocationCoordinates}
						className="account-creation-button"
					>
						Add another set of coordinates
					</button>
				</div>
			</div>
			<div>
				{/* Radio button to determine if company exists */}
				<label className="acc-create-container">
					<input
						type="radio"
						name="companyExists"
						value="yes"
						checked={companyExists}
						onChange={handleCompanyExistsChange}
					/>
					<div className="acc-create-box">
						<span>Update a Company </span>
					</div>
				</label>
				<label className="acc-create-container">
					<input
						type="radio"
						name="companyExists"
						value="no"
						checked={!companyExists}
						onChange={handleCompanyExistsChange}
					/>
					<div className="acc-create-box">
						<span>Create a Company</span>
					</div>
				</label>
			</div>
			{/* Dropdown for selecting existing company */}

			<div className="company-selectors-container">
				{companyExists ? (
					<div className="company-exist-dropdown-container">
						<div className="company-exists-inputs">
							<input
								type="text"
								placeholder={placeholder}
								value={searchInput || ''}
								onChange={handleSearchChange}
								className="search-input"
								id="company-search-input"
							/>
							<div className="hubspot-createacc-display">
								<div className="hubspot-display-container">
									{hsidForDisplay && (
										<div>
											<p className="hubspot-header-title">
												HubspotId:
											</p>
											<p className="hubspot-company-exists-display-number">
												{hsidForDisplay}
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
						{/* Displays the dropdown of companies if we meet certain conditions */}
						{isDropdownVisible && filteredCompanies.length > 0 && (
							<ul className="acc-create-dropdown-list">
								{filteredCompanies.map(company => (
									<li
										key={company.id}
										onClick={() => {
											handleSelectCompany(company);
										}}
									>
										{company.name}
									</li>
								))}
							</ul>
						)}
						{/* Displays our selected company below our search */}
						{selectedCompany && (
							<div className="selected-company">
								Selected Company: {selectedCompany}
							</div>
						)}
					</div>
				) : (
					<div className="company-does-not-exist-inputs-outer-container">
						<div className="company-does-not-exist-inputs-container">
							{/* If the company doesn't already exist we want to create a new one, enter the name into the input field */}

							<HubspotCompanySelector
								allCompanies={companies}
								selectHubspotCompany={(
									company: Hscompany | null
								) => {
									setCompanyHsId(company?.companyHsId ?? '');
									setSelectedCompany(
										company?.companyName ?? ''
									);
								}}
							/>

							<div className="hubspot-createacc-display">
								<div className="hubspot-display-container">
									{companyHsId && (
										<div>
											<p className="hubspot-header-title">
												HubspotId:
											</p>
											<p className="hubspot-company-exists-display-number">
												{companyHsId}
											</p>
										</div>
									)}
								</div>
							</div>

							<div className="message-box-informative">
								<p>
									If the company{' '}
									<span className="highlight">
										Hubspot Company
									</span>{' '}
									doesn't show up, it might be because it
									already exists, go to
									<span className="update-link">
										{' '}
										Update a company
									</span>{' '}
									instead!
								</p>
								<p>
									For more information, refer to{' '}
									<a
										className="notion-link"
										href="https://www.notion.so/CS-Best-Practices-for-contact-Company-Creation-Post-Integration-with-ChurnZero-and-CacheFlow-135c591b758980e58fe9f7f880e71a48?pvs=4"
									>
										this Notion document
									</a>
									.
								</p>
							</div>
						</div>
					</div>
				)}
			</div>
			{/* Our submit button to start the account creation process */}
			<div className="acc-create-submite-button-container">
				<button
					className="admin-create-cancel-button"
					onClick={() => createAccount()}
				>
					Create Account(s)
				</button>
				{createdUsersLoading && <p>Loading...</p>}
				{!createdUsersLoading &&
					!createdUsersError &&
					createdUsersData &&
					createdUsersData.length > 0 && (
						<p> Accounts created successfully!<br /></p>
					)}
				{createdCompany && <p> Company created successfully!<br /> </p>}
				{updateCompanyData && (
					<p> Company markets updated successfully!<br /> </p>
				)}
				{displayError && <p>User Error: {displayError}<br /></p>}
				{createdCompanyError?.message && (
					<p>
						Error creating company: {createdCompanyError.message}\n
					</p>
				)}

				{updateCompanyError?.message && (
					<p>
						Error updating company: {updateCompanyError.message}\n
					</p>
				)}

				{missingHsIdmessage && (
					<p className="missing-hsid-error-message">
						{missingHsIdmessage}<br />
					</p>
				)}
			</div>
		</div>
	);
};

export default AdminAccountCreate;
